import { useRef, useState } from "react";

const RULES = /** @type { const } */ (["required"]);

/**
 *
 * @param {*} values
 * @param {{[fieldName: string]: import("../..").ArrayToTuple<typeof RULES>}} rules
 * @returns
 */
const handleRules = (values = {}, rules = {}) => {
  const error = {};
  Object.keys(rules).forEach(fieldName => {
    const value = values[fieldName];
    switch (rules[fieldName]) {
      case RULES[0]:
        if (value === null || value === "" || typeof value === "undefined") {
          error[fieldName] = RULES[0];
        }
        break;
      default:
        break;
    }
  });
  return error;
};

/**
 *
 * @param {string} fieldName
 * @param {any} value
 * @param {{[fieldName: string]: import("../..").ArrayToTuple<typeof RULES>}} error
 * @returns
 */
const updateError = (fieldName, value, error = {}) => {
  if (value === null || value === "" || typeof value === "undefined") {
    error[fieldName] = RULES[0];
  } else {
    delete error[fieldName];
  }
  return error;
};

class FormStore {
  formState = {
    error: {},
    submitted: false,
  };
  data = {};
  rules = {};
  initialValues = {};

  constructor({ initialValues = {}, rules = {}, forceUpdate = () => {} }) {
    this.forceUpdate = forceUpdate;
    this.data = initialValues;
    this.initialValues = { ...initialValues };
    this.rules = rules;
    this.formState.setFieldValue = this.setFieldValue.bind(this);
    this.formState.setFieldsValue = this.setFieldsValue.bind(this);
    this.formState.resetFields = this.resetFields.bind(this);
    this.formState.getFieldValue = this.getFieldValue.bind(this);
    this.formState.handleSubmit = this.handleSubmit.bind(this);
    this.formState.getFormStore = this.getFormStore.bind(this);
    this.formState.updateFormRules = this.updateFormRules.bind(this);
    this.formState.getFieldsValue = this.getFieldsValue.bind(this);
    this.formState.setFieldError = this.setFieldError.bind(this);
    this.formState.markSubmitted = this.markSubmitted.bind(this);
  }
  /**
   *
   * @param {string} fieldName
   * @param {any} data
   */
  setFieldValue(fieldName, data) {
    this.data[fieldName] = data;
    updateError(fieldName, data, this.formState.error);
    this.forceUpdate();
  }

  /**
   *
   * @param {{[fieldName: string]: any}} data
   */
  setFieldsValue(data = {}) {
    for (const fieldName in data) {
      this.setFieldValue(fieldName, data[fieldName]);
    }
    this.forceUpdate();
  }
  resetFields() {
    this.data = { ...this.initialValues };
    this.formState.error = {};
    this.formState.submitted = false;
    this.forceUpdate();
  }

  setFieldError(fieldName, errorCode) {
    if (errorCode) {
      this.formState.error[fieldName] = errorCode;
    } else {
      delete this.formState.error[fieldName];
    }
    this.forceUpdate();
  }

  markSubmitted() {
    this.formState.submitted = true;
    this.forceUpdate();
  }

  handleSubmit(onSubmit, onValidateError) {
    this.formState.submitted = true;
    this.formState.error = handleRules(this.data, this.rules);
    this.forceUpdate();
    if (Object.keys(this.formState.error).length !== 0) {
      onValidateError(this.formState.error);
      return;
    }
    onSubmit(this.data);
  }

  getFieldValue(fieldName) {
    return this.data[fieldName];
  }
  getFieldsValue() {
    return this.data;
  }
  getFormStore() {
    return this.formState;
  }
  updateFormRules(rules = {}) {
    this.rules = { ...this.rules, ...rules };
    this.forceUpdate();
  }
}

/**
 *
 * @param {{[fieldName: string]: any}} initialValues
 * @param {{[fieldName: string]: import("../..").ArrayToTuple<typeof RULES>}} rules
 * @returns {{
 *  error: {[fieldName: string]: import("../..").ArrayToTuple<typeof RULES>},
 *  setFieldValue: (fieldName: string, data: any) => void,
 *  setFieldsValue: ({[fieldName: string]: any}) => void,
 *  getFieldValue: (fieldName: string) => any,
 *  resetFields: () => void,
 *  handleSubmit: (onSubmit: (data: object) => void) => void,
 *  updateFormRules: ({[key: string]: import("../..").ArrayToTuple<typeof RULES>}) => void,
 * }}
 */
const useForm = (initialValues = {}, rules = {}) => {
  const formRef = useRef();
  const [, forceUpdate] = useState({});
  if (!formRef.current) {
    const formStore = new FormStore({
      rules,
      initialValues,
      forceUpdate: () => forceUpdate({}),
    });
    formRef.current = formStore.getFormStore();
  }
  return formRef.current;
};

export default useForm;
